import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, orderBy, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useAuth } from '../../auth/components/AuthProvider';
import { CheckCircleIcon, XCircleIcon, ClockIcon, ExclamationCircleIcon } from '@heroicons/react/outline';

/**
 * Panel de administración para revisar y gestionar reportes de errores en información de Boostr
 */
const AdminReportesErroresBoostr = () => {
  const { user } = useAuth();
  const [reportes, setReportes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('pendiente'); // 'pendiente' | 'resuelto' | 'rechazado' | 'todos'
  const [reporteSeleccionado, setReporteSeleccionado] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    pendiente: 0,
    resuelto: 0,
    rechazado: 0
  });

  useEffect(() => {
    cargarReportes();
    cargarEstadisticas();
  }, [filtro]);

  const cargarEstadisticas = async () => {
    try {
      const reportesSnapshot = await getDocs(collection(db, 'reportes_errores_boostr'));
      
      const statsCount = {
        total: 0,
        pendiente: 0,
        resuelto: 0,
        rechazado: 0
      };

      reportesSnapshot.docs.forEach(doc => {
        const data = doc.data();
        statsCount.total++;
        const estado = data.estado || 'pendiente';
        if (statsCount.hasOwnProperty(estado)) {
          statsCount[estado]++;
        }
      });

      setStats(statsCount);
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
    }
  };

  const cargarReportes = async () => {
    try {
      setLoading(true);
      let q;

      if (filtro === 'todos') {
        q = query(
          collection(db, 'reportes_errores_boostr'),
          orderBy('fechaReporte', 'desc')
        );
      } else {
        q = query(
          collection(db, 'reportes_errores_boostr'),
          where('estado', '==', filtro),
          orderBy('fechaReporte', 'desc')
        );
      }

      const snapshot = await getDocs(q);
      const reportesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setReportes(reportesData);
    } catch (error) {
      console.error('Error cargando reportes:', error);
    } finally {
      setLoading(false);
    }
  };

  const actualizarEstadoReporte = async (reporteId, nuevoEstado, comentario = '') => {
    try {
      const reporteRef = doc(db, 'reportes_errores_boostr', reporteId);
      await updateDoc(reporteRef, {
        estado: nuevoEstado,
        comentarioAdmin: comentario,
        fechaResolucion: serverTimestamp(),
        resueltoPor: user?.uid
      });

      // Recargar reportes
      await cargarReportes();
      await cargarEstadisticas();
      setReporteSeleccionado(null);
      
      alert(`✅ Reporte marcado como ${nuevoEstado}`);
    } catch (error) {
      console.error('Error actualizando reporte:', error);
      alert('Error al actualizar el reporte');
    }
  };

  const getEstadoIcon = (estado) => {
    switch (estado) {
      case 'pendiente':
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case 'resuelto':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'rechazado':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <ExclamationCircleIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'pendiente':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'resuelto':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'rechazado':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getSeccionLabel = (seccion) => {
    const secciones = {
      'informacion_basica': 'Información Básica',
      'revision_tecnica': 'Revisión Técnica',
      'soap': 'SOAP',
      'multas': 'Multas',
      'tag': 'Deuda TAG',
      'pasos_sin_tag': 'Pasos sin TAG',
      'recalls': 'Recalls',
      'otro': 'Otro'
    };
    return secciones[seccion] || seccion;
  };

  if (loading && reportes.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">📋 Reportes de Errores - Información Boostr</h2>
        
        {/* Estadísticas */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-gray-600">Total</p>
            <p className="text-2xl font-bold text-blue-700">{stats.total}</p>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-gray-600">Pendientes</p>
            <p className="text-2xl font-bold text-yellow-700">{stats.pendiente}</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm text-gray-600">Resueltos</p>
            <p className="text-2xl font-bold text-green-700">{stats.resuelto}</p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-gray-600">Rechazados</p>
            <p className="text-2xl font-bold text-red-700">{stats.rechazado}</p>
          </div>
        </div>

        {/* Filtros */}
        <div className="flex gap-2 flex-wrap">
          {['pendiente', 'resuelto', 'rechazado', 'todos'].map((filtroOption) => (
            <button
              key={filtroOption}
              onClick={() => setFiltro(filtroOption)}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                filtro === filtroOption
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {filtroOption.charAt(0).toUpperCase() + filtroOption.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Lista de reportes */}
      <div className="space-y-4">
        {reportes.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <p className="text-gray-600">No hay reportes {filtro !== 'todos' ? `con estado "${filtro}"` : ''}</p>
          </div>
        ) : (
          reportes.map((reporte) => (
            <div
              key={reporte.id}
              className={`bg-white rounded-lg shadow-sm border-2 p-6 ${getEstadoColor(reporte.estado)}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  {getEstadoIcon(reporte.estado)}
                  <div>
                    <h3 className="font-bold text-lg">
                      Reporte #{reporte.id.substring(0, 8)}
                    </h3>
                    <p className="text-sm opacity-80">
                      {reporte.fechaReporte?.toDate 
                        ? reporte.fechaReporte.toDate().toLocaleString('es-CL')
                        : 'Fecha no disponible'}
                    </p>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getEstadoColor(reporte.estado)}`}>
                  {reporte.estado.toUpperCase()}
                </span>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm font-medium mb-1">Vehículo</p>
                  <p className="text-sm">Patente: <strong>{reporte.patente}</strong></p>
                  <p className="text-xs text-gray-600">ID: {reporte.vehiculoId}</p>
                </div>
                <div>
                  <p className="text-sm font-medium mb-1">Sección con error</p>
                  <p className="text-sm font-semibold">{getSeccionLabel(reporte.seccion)}</p>
                </div>
              </div>

              <div className="mb-4">
                <p className="text-sm font-medium mb-1">Descripción del error</p>
                <p className="text-sm bg-white bg-opacity-50 rounded-lg p-3">{reporte.descripcion}</p>
              </div>

              {reporte.comentarioAdmin && (
                <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-blue-900 mb-1">Comentario del administrador</p>
                  <p className="text-sm text-blue-800">{reporte.comentarioAdmin}</p>
                </div>
              )}

              {reporte.estado === 'pendiente' && (
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => {
                      const comentario = prompt('Comentario (opcional):');
                      actualizarEstadoReporte(reporte.id, 'resuelto', comentario || '');
                    }}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                  >
                    ✅ Marcar como Resuelto
                  </button>
                  <button
                    onClick={() => {
                      const comentario = prompt('Motivo del rechazo (opcional):');
                      actualizarEstadoReporte(reporte.id, 'rechazado', comentario || '');
                    }}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
                  >
                    ❌ Rechazar
                  </button>
                  <button
                    onClick={() => setReporteSeleccionado(reporte)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                  >
                    👁️ Ver Detalles
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Modal de detalles */}
      {reporteSeleccionado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Detalles del Reporte</h3>
              <button
                onClick={() => setReporteSeleccionado(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Datos actuales en el sistema</p>
                <pre className="bg-gray-50 rounded-lg p-4 text-xs overflow-x-auto">
                  {JSON.stringify(reporteSeleccionado.datosActuales, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReportesErroresBoostr;


